import React from "react";
import styled from "styled-components";
import { menuData } from "../data/MenuData";
import { Link } from "react-router-dom";
import { FaTimes } from "react-icons/fa";
import { IoMdArrowRoundForward } from "react-icons/io";
import { FaFacebookF, FaInstagram, FaYoutube } from "react-icons/fa";
import logoRata from "../images/logoRata.png";
import { RACE_DATE_LABEL } from "../data/EventConfig";

const Backdrop = styled.div`
  position: fixed;
  inset: 0;
  z-index: 998;
  background: rgba(10, 42, 74, 0.55);
  backdrop-filter: blur(4px);
  opacity: ${({ $open }) => ($open ? 1 : 0)};
  pointer-events: ${({ $open }) => ($open ? "auto" : "none")};
  transition: opacity 0.25s ease;
`;

const Panel = styled.aside`
  position: fixed;
  top: 0;
  right: 0;
  height: 100vh;
  width: min(360px, 88vw);
  z-index: 999;
  background: linear-gradient(180deg, #0a2a4a 0%, #061a30 100%);
  color: #fff;
  display: flex;
  flex-direction: column;
  transform: ${({ $open }) =>
    $open ? "translateX(0)" : "translateX(105%)"};
  transition: transform 0.32s cubic-bezier(0.22, 0.61, 0.36, 1);
  box-shadow: -20px 0 60px rgba(0, 0, 0, 0.4);
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1.25rem 1.5rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
`;

const Brand = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;

  img {
    width: 40px;
    height: 40px;
    object-fit: contain;
  }

  div {
    line-height: 1.1;
  }

  strong {
    color: #ffce2d;
    font-size: 0.95rem;
    font-weight: 800;
  }

  span {
    display: block;
    color: rgba(255, 255, 255, 0.6);
    font-size: 0.72rem;
    margin-top: 2px;
  }
`;

const CloseBtn = styled.button`
  background: rgba(255, 255, 255, 0.08);
  border: none;
  color: #fff;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background 0.2s ease;

  &:hover {
    background: rgba(255, 206, 45, 0.18);
    color: #ffce2d;
  }
`;

const NavList = styled.nav`
  flex: 1;
  overflow-y: auto;
  padding: 1.5rem 0.5rem;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

const itemStyles = `
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.95rem 1.25rem;
  border-radius: 12px;
  color: rgba(255, 255, 255, 0.92);
  text-decoration: none;
  font-size: 1.05rem;
  font-weight: 600;
  transition: background 0.2s ease, color 0.2s ease, transform 0.15s ease;
  cursor: pointer;

  &:hover, &:focus-visible {
    background: rgba(255, 206, 45, 0.12);
    color: #ffce2d;
    outline: none;
    transform: translateX(2px);
  }
`;

const Item = styled(Link)`
  ${itemStyles}
`;

const ExtItem = styled.a`
  ${itemStyles}
`;

const Footer = styled.div`
  padding: 1.25rem 1.5rem 1.75rem;
  border-top: 1px solid rgba(255, 255, 255, 0.08);
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const CTA = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  background: rgba(250, 167, 30, 0.18);
  color: #ffce2d;
  padding: 0.9rem 1.25rem;
  border: 1px dashed rgba(255, 206, 45, 0.6);
  border-radius: 999px;
  font-weight: 800;
  font-size: 1rem;
  cursor: default;
  user-select: none;
  font-family: inherit;
`;

const Socials = styled.div`
  display: flex;
  gap: 0.6rem;
  justify-content: center;
`;

const Social = styled.a`
  width: 38px;
  height: 38px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.08);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: rgba(255, 255, 255, 0.85);
  text-decoration: none;
  transition: background 0.2s ease, color 0.2s ease;

  &:hover {
    background: #ffce2d;
    color: #0a2a4a;
  }
`;

const Dropdown = ({ isOpen, toggle }) => {
  return (
    <>
      <Backdrop $open={isOpen} onClick={toggle} />
      <Panel $open={isOpen} aria-hidden={!isOpen}>
        <Header>
          <Brand>
            <img src={logoRata} alt="" />
            <div>
              <strong>Cursa de Rățuște</strong>
              <span>{RACE_DATE_LABEL}</span>
            </div>
          </Brand>
          <CloseBtn onClick={toggle} aria-label="Închide meniu">
            <FaTimes size={16} />
          </CloseBtn>
        </Header>

        <NavList>
          {menuData.map((item, i) => {
            const isExternal = /^https?:/.test(item.link);
            if (isExternal) {
              return (
                <ExtItem
                  key={i}
                  href={item.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={toggle}
                >
                  <span>{item.title}</span>
                  <IoMdArrowRoundForward size={18} />
                </ExtItem>
              );
            }
            return (
              <Item key={i} to={item.link} onClick={toggle}>
                <span>{item.title}</span>
                <IoMdArrowRoundForward size={18} />
              </Item>
            );
          })}
        </NavList>

        <Footer>
          <CTA aria-disabled="true">🔒 Ne vedem la anul!</CTA>
          <Socials>
            <Social
              href="https://www.facebook.com/RotaractClubTimisoara"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Facebook"
            >
              <FaFacebookF size={14} />
            </Social>
            <Social
              href="https://www.instagram.com/rotaractclubtimisoara/"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Instagram"
            >
              <FaInstagram size={14} />
            </Social>
            <Social
              href="https://www.youtube.com/results?search_query=cursa+ratustelor+pe+bega"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="YouTube"
            >
              <FaYoutube size={14} />
            </Social>
          </Socials>
        </Footer>
      </Panel>
    </>
  );
};

export default Dropdown;
